"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTaskBoard = void 0;
const react_1 = require("react");
const workflow_core_1 = require("@sierralabs/workflow-core");
const useStateNodeByStatus_1 = require("../../hooks/useStateNodeByStatus");
const useWorkflowItem_1 = require("../../hooks/useWorkflowItem");
function toColumns(states, tasks) {
    return states.map(state => {
        return {
            state,
            tasks: tasks.filter(task => state.name === (0, workflow_core_1.parseTaskStatus)(task.status))
        };
    });
}
function useTaskBoardColumns(taskTypeId, tasks) {
    const options = (0, react_1.useMemo)(() => ({ taskTypeId }), [taskTypeId]);
    const item = (0, useWorkflowItem_1.useWorkflowItem)(options);
    const { workflowVersion } = item || {};
    const states = (0, react_1.useMemo)(() => {
        var _a;
        return ((_a = workflowVersion === null || workflowVersion === void 0 ? void 0 : workflowVersion.config) === null || _a === void 0 ? void 0 : _a.states) || [];
    }, [workflowVersion]);
    const [columns, setColumns] = (0, react_1.useState)(() => toColumns(states, tasks || []));
    (0, react_1.useEffect)(() => {
        setColumns(toColumns(states, tasks || []));
    }, [states, tasks]);
    return { item, states, columns, setColumns };
}
function getTaskIndex(columns, taskId) {
    for (const [columnIndex, column] of columns.entries()) {
        for (const [taskIndex, task] of column.tasks.entries()) {
            if (task.id === taskId) {
                return { column: columnIndex, task: taskIndex };
            }
        }
    }
}
function useTaskBoard(taskTypeId, tasks) {
    const { item, states, columns, setColumns } = useTaskBoardColumns(taskTypeId, tasks);
    const columnsRef = (0, react_1.useRef)(columns);
    const [loading, setLoading] = (0, react_1.useState)([]);
    const isLoading = (0, react_1.useCallback)((taskId) => loading.includes(taskId), [loading]);
    const setIsLoading = (0, react_1.useCallback)((taskId, isLoading) => {
        setLoading(loading => {
            const index = loading.indexOf(taskId);
            if (index < 0 && isLoading) {
                return loading.concat(taskId);
            }
            else if (index > -1 && !isLoading) {
                const copy = Array.from(loading);
                copy.splice(index, 1);
                return copy;
            }
            return loading;
        });
    }, []);
    const findEvent = (0, react_1.useCallback)((taskStatus, targetStatus) => {
        var _a;
        taskStatus = (0, workflow_core_1.parseTaskStatus)(taskStatus);
        const state = states.find(state => state.name === taskStatus);
        return (_a = state === null || state === void 0 ? void 0 : state.events) === null || _a === void 0 ? void 0 : _a.find(event => {
            return event.transitions.some(transition => targetStatus === (0, workflow_core_1.parseTaskStatus)(transition.target));
        });
    }, [states]);
    const findTask = (0, react_1.useCallback)((taskId) => {
        // get the task reference from columns instead of tasks array
        const index = getTaskIndex(columns, taskId);
        return index ? columns[index.column].tasks[index.task] : undefined;
    }, [columns]);
    const indexOf = (0, react_1.useCallback)((taskId) => getTaskIndex(columns, taskId), [columns]);
    // move task to another column (does not save task status to API)
    const move = (0, react_1.useCallback)((taskId, status, position) => {
        const statusPath = (0, useStateNodeByStatus_1.getStatusPath)(status, item === null || item === void 0 ? void 0 : item.workflow);
        setColumns(columns => {
            const target = columns.findIndex(column => column.state.name === (0, workflow_core_1.parseTaskStatus)(status));
            const index = getTaskIndex(columns, taskId);
            if (!index || target < 0 || index.column === target) {
                return columns;
            }
            // create new copies of column and column.tasks
            const column = columns[index.column];
            const targetColumn = columns[target];
            const task = column.tasks[index.task];
            const newColumn = Object.assign(Object.assign({}, column), { tasks: Array.from(column.tasks) });
            const newTargetColumn = Object.assign(Object.assign({}, targetColumn), { tasks: Array.from(targetColumn.tasks) });
            const newTask = Object.assign({}, task);
            if (statusPath) {
                newTask.status = statusPath;
            }
            // move task to target column
            newColumn.tasks.splice(index.task, 1);
            if (typeof position === 'number') {
                newTargetColumn.tasks.splice(position, 0, newTask);
            }
            else {
                newTargetColumn.tasks.push(newTask);
            }
            // save columns
            const copy = Array.from(columns);
            copy.splice(index.column, 1, newColumn);
            copy.splice(target, 1, newTargetColumn);
            return copy;
        });
    }, [item === null || item === void 0 ? void 0 : item.workflow, setColumns]);
    const refresh = (0, react_1.useCallback)(() => {
        setColumns(columns => {
            // get all tasks and create new columns
            const tasks = [].concat(...columns.map(column => column.tasks));
            return toColumns(states, tasks);
        });
    }, [setColumns, states]);
    const update = (0, react_1.useCallback)((taskId, updated) => {
        setColumns(columns => {
            const index = getTaskIndex(columns, taskId);
            if (!index) {
                return columns;
            }
            const column = columns[index.column];
            const task = column.tasks[index.task];
            // create new copies of column and column.tasks
            const newColumn = Object.assign(Object.assign({}, column), { tasks: Array.from(column.tasks) });
            const newTask = Object.assign(Object.assign({}, task), updated);
            newColumn.tasks.splice(index.task, 1, newTask);
            // save columns
            const copy = Array.from(columns);
            copy.splice(index.column, 1, newColumn);
            return copy;
        });
    }, [setColumns]);
    const manager = (0, react_1.useMemo)(() => {
        if (!item) {
            return;
        }
        return {
            columns,
            states,
            workflow: item,
            findEvent,
            findTask,
            indexOf,
            isLoading,
            move,
            refresh,
            setIsLoading,
            update
        };
    }, [
        columns,
        item,
        states,
        findEvent,
        findTask,
        indexOf,
        isLoading,
        move,
        refresh,
        setIsLoading,
        update
    ]);
    (0, react_1.useEffect)(() => {
        columnsRef.current = columns;
    }, [columns]);
    return manager;
}
exports.useTaskBoard = useTaskBoard;
//# sourceMappingURL=useTaskBoard.js.map